import { ClockIcon } from 'lucide-react';
import * as React from 'react';

import { cn, createTestIdHelper } from '../../../lib/utils';
import { Button } from '../button';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

const testId = createTestIdHelper('time-picker');

type Period = 'AM' | 'PM';

export interface TimePickerProps {
  /** Minutes since midnight (0–1439). Undefined renders the placeholder. */
  value?: number;
  onChange?: (minutesSinceMidnight: number) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Step between selectable minutes. Defaults to 1 (every minute). */
  minuteStep?: number;
  error?: boolean;
  id?: string;
  className?: string;
  align?: React.ComponentProps<typeof PopoverContent>['align'];
  'data-testid'?: string;
  /** Accessible names for the three columns — supplied by the app (i18n). */
  hourLabel?: string;
  minuteLabel?: string;
  periodLabel?: string;
  /** Accessible name for the trigger; without it the name is just the value. */
  'aria-label'?: string;
}

const HOURS_12 = Array.from({ length: 12 }, (_, index) => index + 1);
const PERIODS: Period[] = ['AM', 'PM'];

/**
 * Time counterpart to `DatePicker`: the same outline trigger, the same popover,
 * so a date and a time sitting next to each other read as one control pair.
 *
 * Replaces `<input type="time">`, whose rendering, keyboard model and spinner
 * come from the browser — it looked and behaved nothing like the date field
 * beside it, and varies per platform.
 *
 * The value is MINUTES SINCE MIDNIGHT rather than a Date: a time-of-day has no
 * business carrying a day, and the caller composes it onto whatever date it
 * owns. Display is 12-hour with an explicit AM/PM column.
 */
export function TimePicker({
  value,
  onChange,
  placeholder = '-- : -- --',
  disabled = false,
  minuteStep = 1,
  error = false,
  id,
  className,
  align = 'start',
  'data-testid': dataTestId,
  hourLabel = 'Hour',
  minuteLabel = 'Minute',
  periodLabel = 'AM or PM',
  'aria-label': ariaLabel,
}: TimePickerProps) {
  const [open, setOpen] = React.useState(false);

  const minutes = React.useMemo(
    () =>
      Array.from(
        { length: Math.ceil(60 / Math.max(1, minuteStep)) },
        (_, index) => index * Math.max(1, minuteStep),
      ),
    [minuteStep],
  );

  const parts = value === undefined ? undefined : splitTime(value);

  // Committing one column keeps the other two; with nothing set yet the
  // untouched columns default to 12:00 AM so a single click is a valid time.
  const commit = (next: Partial<ReturnType<typeof splitTime>>) => {
    const base = parts ?? { hour12: 12, minute: 0, period: 'AM' as Period };
    const merged = { ...base, ...next };
    onChange?.(toMinutes(merged.hour12, merged.minute, merged.period));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          data-testid={dataTestId ?? testId('trigger')}
          className={cn(
            // Mirrors DatePicker's trigger so the pair aligns exactly.
            'h-10 w-full justify-start border-input text-left font-normal',
            error && 'border-destructive',
            className,
          )}
        >
          <ClockIcon className="mr-2 size-4 opacity-50" aria-hidden />
          <span className={cn('flex-1', !parts && 'text-muted-foreground')}>
            {parts
              ? `${parts.hour12} : ${String(parts.minute).padStart(2, '0')} ${parts.period}`
              : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[60] w-auto p-1"
        align={align}
        sideOffset={4}
        data-testid={testId('panel')}
      >
        <div className="flex gap-1">
          <Column
            label={hourLabel}
            options={HOURS_12.map((hour) => ({
              key: hour,
              label: String(hour),
            }))}
            selected={parts?.hour12}
            onPick={(hour12) => commit({ hour12 })}
            testKey="hour"
          />
          <Column
            label={minuteLabel}
            options={minutes.map((minute) => ({
              key: minute,
              label: String(minute).padStart(2, '0'),
            }))}
            selected={parts?.minute}
            onPick={(minute) => commit({ minute })}
            testKey="minute"
          />
          <Column
            label={periodLabel}
            options={PERIODS.map((period) => ({
              key: period,
              label: period,
            }))}
            selected={parts?.period}
            onPick={(period) => commit({ period })}
            testKey="period"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface ColumnProps<T extends string | number> {
  label: string;
  options: { key: T; label: string }[];
  selected: T | undefined;
  onPick: (key: T) => void;
  testKey: string;
}

function Column<T extends string | number>({
  label,
  options,
  selected,
  onPick,
  testKey,
}: ColumnProps<T>) {
  return (
    <div
      role="listbox"
      aria-label={label}
      // Height is capped so the three columns scroll independently instead of
      // making the popover taller than the viewport at minuteStep=1.
      className="thin-scrollbar max-h-56 w-14 overflow-y-auto"
      data-testid={testId(`${testKey}-column`)}
    >
      {options.map((option) => {
        const isSelected = option.key === selected;
        return (
          <button
            key={option.key}
            type="button"
            role="option"
            aria-selected={isSelected}
            onClick={() => onPick(option.key)}
            data-testid={testId(`${testKey}-option`)}
            className={cn(
              'w-full rounded px-2 py-1 text-center text-sm transition-colors',
              isSelected
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Minutes since midnight → 12-hour parts. 0 → 12:00 AM, 780 → 1:00 PM. */
function splitTime(minutesSinceMidnight: number): {
  hour12: number;
  minute: number;
  period: Period;
} {
  const normalized = ((minutesSinceMidnight % 1440) + 1440) % 1440;
  const hour24 = Math.floor(normalized / 60);
  return {
    hour12: hour24 % 12 === 0 ? 12 : hour24 % 12,
    minute: normalized % 60,
    period: hour24 < 12 ? 'AM' : 'PM',
  };
}

/** 12-hour parts → minutes since midnight. 12 AM → 0, 12 PM → 720. */
function toMinutes(hour12: number, minute: number, period: Period): number {
  const base = hour12 % 12;
  const hour24 = period === 'PM' ? base + 12 : base;
  return hour24 * 60 + minute;
}
